import { Children, useEffect, useState, type ReactNode } from "react";
import { Button } from "../components/Button";
import { displayName, shortName } from "../components/profile-ui";
import { fa } from "../i18n/fa";
import type { LeavePenaltyInfo, LeftInfo, PausedInfo, TurnInfo } from "./online-connection";
import type { LobbyState } from "./online-connection";

/* ------------------------------------------------------------------ *
 * In-game multiplayer lifecycle overlays, shared by every online table —
 * ported from the "Bing card-room" Frame ۱۰: a stroked `timerRing`
 * countdown pinned over the table, and a `pausedFrame` reconnect card.
 * ------------------------------------------------------------------ */

const FA_DIGITS = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"] as const;
/** Latin→Persian digits for the on-ring seconds readout (cleaner RTL look). */
function faNum(value: number): string {
  return String(value).replace(/\d/g, (d) => FA_DIGITS[Number(d)] ?? d);
}

/**
 * The design's `timerRing` — an SVG progress ring that empties as `pct` (the
 * fraction of time left) drops, with a centered child (the seconds readout).
 */
function TimerRing({
  size,
  pct,
  color,
  children,
}: {
  size: number;
  pct: number;
  color: string;
  children: ReactNode;
}): ReactNode {
  const r = size / 2 - 3;
  const c = 2 * Math.PI * r;
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden
        style={{ position: "absolute", inset: 0, transform: "rotate(-90deg)" }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--score-chip-bg)"
          strokeWidth={2.25}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={2.25}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
        />
      </svg>
      <div style={{ position: "absolute", inset: 4, display: "grid", placeItems: "center" }}>
        {children}
      </div>
    </div>
  );
}

/** The live turn/pass timer the shell threads into a table via `renderTable`'s `timer`, so the
 *  ring anchors to the table (حکم and بی‌دل: `TurnTimerSlot` in your strip) instead of floating in
 *  the shell. */
export interface TableTurnTimer {
  turn: TurnInfo;
  you: number | null;
  totalSec: number;
}

/**
 * A small shrinking gold ring counting the current turn down to its `endsAt` deadline —
 * POSITION-AGNOSTIC: each table places it (حکم and بی‌دل in {@link TurnTimerSlot}; منچ and دوز
 * beside their own pieces). Turns amber (your turn OR a shared pass window, seat < 0) and red in
 * the final seconds; `totalSec` sets the fullness. `compact` drops the text label to a ring-only
 * pill.
 */
export function TurnTimer({
  turn,
  you,
  totalSec,
  compact = false,
}: TableTurnTimer & { compact?: boolean }): ReactNode {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(id);
  }, []);

  const remainingMs = Math.max(0, turn.endsAt - now);
  /*
   * HOW LONG THIS WINDOW IS — TAKEN FROM THE FRAME, NOT ASSUMED FROM THE SETTING.
   *
   * `totalSec` is the table's CONFIGURED turn length, which is not always the length of the window
   * on screen. When the server armed a longer one, both readings below clamped to the setting and
   * sat pinned at their maximum until the remaining time fell back under it — the ring simply
   * STOPPED, which is exactly how the frozen-countdown-on-a-double bug looked. `turn.totalMs` is
   * the armed window itself; the fallback is for a server that predates the field.
   */
  const windowMs = turn.totalMs ?? totalSec * 1000;
  // ISSUE 4 — a 10s turn sometimes flashed «۱۱»: `endsAt` is the server's clock, and if it is a
  // few ms ahead of this device `remainingMs` starts just over 10 000, so `ceil` rounds to 11.
  // Cap the DISPLAYED seconds at the window's own length — the clock can never read more than the
  // window is long. The ring fraction below is clamped to 1 for the same reason.
  const secs = Math.min(Math.ceil(windowMs / 1000), Math.ceil(remainingMs / 1000));
  const frac = windowMs > 0 ? Math.max(0, Math.min(1, remainingMs / windowMs)) : 0;
  const yours = you !== null && turn.seat === you;
  // A shared passing window (Hearts' simultaneous 3-card pass) is broadcast with a
  // seat < 0 — it's EVERYONE's window, so it reads as active for all players.
  const shared = turn.seat < 0;
  const active = shared || yours;
  const low = remainingMs <= 5000;
  const ringColor = low ? "var(--danger)" : active ? "var(--accent)" : "var(--felt)";
  const label = active && !shared ? fa.online.yourTurn : fa.online.turnLeft;

  return (
    <div
      aria-label={label}
      style={{
        display: "flex",
        alignItems: "center",
        gap: compact ? 0 : 5,
        padding: compact ? 3 : "3px 9px 3px 3px",
        borderRadius: 999,
        background: "color-mix(in srgb, var(--surface) 90%, transparent)",
        border: "1px solid var(--hairline)",
        boxShadow: "0 4px 12px rgba(0,0,0,.3)",
        backdropFilter: "blur(8px)",
      }}
    >
      {/* Same ring size in BOTH variants so the passing (compact) timer and the turn
          timer look identical — only the text label + surrounding pill padding differ. */}
      <TimerRing size={24} pct={frac} color={ringColor}>
        <span
          style={{ fontSize: 10.5, fontWeight: 800, color: low ? "var(--danger)" : "var(--text)" }}
        >
          {faNum(secs)}
        </span>
      </TimerRing>
      {compact ? null : (
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            color: active ? "var(--accent)" : "var(--hint)",
          }}
        >
          {label}
        </span>
      )}
    </div>
  );
}

/**
 * THE TURN TIMER'S ONE FIXED SLOT — حکم's since 2026-10-03 (operator: «the turn timer must always
 * sit in one fixed place, for every player and every screen size», «it shouldn't move around»),
 * بی‌دل's since 2026-10-08 («the turn timer gets one fixed slot opposite the player's profile, as in
 * حکم»). ONE component, so the two tables cannot drift apart.
 *
 * The table renders it as the LAST child of its strip's right-hand group — the strip's physical LEFT
 * end under rtl, opposite your avatar at its right end — so its place depends on nothing it shows:
 * not your name, not the status pill's words, not whose turn it is, not the phase.
 *
 * RESERVED IFF THE TABLE WAS HANDED A `timer` AT ALL. The online shell ALWAYS passes one — the
 * countdown, or `null` while nobody's turn is timed (حکم's deal, between turns) — and solo never
 * does, so the slot is there from the first frame of every online table and never in solo: nothing
 * in the strip moves when a countdown comes or goes. (Reserving it from the first SEEN countdown, as
 * حکم's round 2 did, still shifted the pill 38 px once per match.)
 *
 * The COMPACT ring: digits only, its words kept as the accessible name — the status pill beside it
 * already says «نوبت شماست», and one row must not say it twice. `hold` keeps the ring back while the
 * slot stays: حکم's deal-until-Ace, where a ring that reads «نوبت شماست» on ONE screen would name the
 * حاکم before the reveal does.
 */
export function TurnTimerSlot({
  timer,
  hold = false,
}: {
  timer: TableTurnTimer | null | undefined;
  hold?: boolean;
}): ReactNode {
  if (timer === undefined) return null;
  return (
    <div
      data-testid="turn-timer-slot"
      className="flex h-8 w-8 flex-none items-center justify-center"
    >
      {timer && !hold ? (
        <TurnTimer turn={timer.turn} you={timer.you} totalSec={timer.totalSec} compact />
      ) : null}
    </div>
  );
}

/**
 * Full-screen overlay shown when a player drops and the table is paused waiting
 * for them (the `"pause"` abandon policy) — the design's `pausedFrame`: a dim
 * scrim, a top "reconnecting" pill, and a card that names who dropped. The host
 * can convert them to a bot and continue; everyone else waits.
 */
export function PausedOverlay({
  paused,
  isHost,
  onResume,
}: {
  paused: PausedInfo;
  isHost: boolean;
  onResume: () => void;
}): ReactNode {
  return (
    <div
      role="alertdialog"
      aria-label={fa.online.paused}
      style={{ position: "fixed", inset: 0, zIndex: 40, background: "rgba(0,0,0,.55)" }}
    >
      {/* reconnecting pill */}
      <div
        style={{
          position: "absolute",
          top: "calc(max(env(safe-area-inset-top), var(--tg-safe-top, 0px)) + 16px)",
          // PHYSICAL left — `insetInlineStart` resolves to `right` in this RTL app, so
          // `right: 50%` + `translateX(-50%)` parked the pill about its own width LEFT of
          // centre: the "stuck in the corner" notice. (Same bug `LeftToast` already had.)
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 14px",
          borderRadius: 999,
          background: "var(--surface)",
          border: "1px solid var(--hairline)",
          fontSize: 12.5,
          fontWeight: 700,
          boxShadow: "0 6px 18px rgba(0,0,0,.3)",
          whiteSpace: "nowrap",
        }}
      >
        <span
          aria-hidden
          style={{
            width: 15,
            height: 15,
            borderRadius: "50%",
            border: "2px solid var(--hairline)",
            borderTopColor: "var(--accent)",
            animation: "bg-spin 0.8s linear infinite",
          }}
        />
        {fa.online.paused}
      </div>

      {/* reconnect card */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "grid",
          placeItems: "center",
          padding: 22,
        }}
      >
        <div
          style={{
            background: "var(--surface)",
            borderRadius: "var(--radius-lg)",
            padding: "26px 22px",
            width: "100%",
            maxWidth: 320,
            textAlign: "center",
            border: "1px solid var(--hairline)",
            boxShadow: "0 24px 60px rgba(0,0,0,.5)",
            animation: "bg-pop 0.4s ease",
            display: "grid",
            gap: 8,
            justifyItems: "center",
          }}
        >
          <div
            aria-hidden
            style={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              marginBottom: 6,
              border: "3px solid var(--hairline)",
              borderTopColor: "var(--accent)",
              animation: "bg-spin 1s linear infinite",
            }}
          />
          <p style={{ margin: 0, fontWeight: 800, fontSize: 18 }}>
            {fa.online.playerDisconnected(shortName(paused.name))}
          </p>
          {isHost ? (
            <Button variant="primary" onClick={onResume} style={{ width: "100%", marginTop: 8 }}>
              {fa.online.resumeWithBot}
            </Button>
          ) : (
            <p className="muted" style={{ margin: "6px 0 0", fontSize: 12.5 }}>
              {fa.online.waitingForHost}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/** ms → `m:ss` in Persian digits (the reconnect grace counts down minutes:seconds). */
function faCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${faNum(m)}:${faNum(s).padStart(2, "۰")}`;
}

/**
 * CHESS (Phase 3c) — the "opponent is away" overlay, shown to the PRESENT player for all three
 * disconnect phases (spec §9). One adaptive card so the chess disconnect UX is consistent (the card
 * games' {@link PausedOverlay} with its resume-with-bot button never applies to chess):
 *  - **reconnecting** (grace): spinner + `title` + a live `endsAt` countdown («mm:ss»).
 *  - **paused** (pause policy, waiting for the max-pause): spinner + `title` (no countdown, no claim).
 *  - **claimable** (grace expired / max-pause elapsed): a flag + `title` + a «ادعای برد» button
 *    (the present player wins, or a draw if they can't mate — the server decides).
 * The gone player's own client is offline, so only the present player ever sees this.
 */
export function OpponentAwayOverlay({
  title,
  hint,
  endsAt,
  onClaim,
}: {
  title: string;
  hint?: string;
  /** When set, a live countdown to this epoch deadline (the reconnect grace). */
  endsAt?: number;
  /** When set, a «ادعای برد» button that ends the game in the present player's favour (claimable). */
  onClaim?: () => void;
  /*
   * ⚠️ NO `onLeave` HERE ANY MORE (operator, 2026-09-15). This overlay used to carry the table's
   * way out, because as a full-screen scrim it COVERED the real exit at `z-10`. It is no longer
   * used for the wait at all — that is a `pointer-events:none` pill now — and the only thing it
   * still renders is the CLAIM card, which is an action rather than a wait and deliberately shows
   * one button. The exit is taken on the table itself, whose dialog follows `leaveIsFree`.
   */
}): ReactNode {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (endsAt === undefined) return;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [endsAt]);
  const remaining = endsAt === undefined ? 0 : Math.max(0, endsAt - now);
  return (
    <div
      role="alertdialog"
      aria-label={title}
      style={{ position: "fixed", inset: 0, zIndex: 40, background: "rgba(0,0,0,.55)" }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "grid",
          placeItems: "center",
          padding: 22,
        }}
      >
        <div
          className={onClaim ? "end-card end-card--win" : undefined}
          style={{
            background: "var(--surface)",
            borderRadius: "var(--radius-lg)",
            padding: "26px 22px",
            width: "100%",
            maxWidth: 320,
            textAlign: "center",
            border: "1px solid var(--hairline)",
            boxShadow: "0 24px 60px rgba(0,0,0,.5)",
            animation: "bg-pop 0.4s ease",
            display: "grid",
            gap: 8,
            justifyItems: "center",
          }}
        >
          {onClaim ? (
            <div style={{ fontSize: 44 }} aria-hidden>
              🏳️
            </div>
          ) : (
            <div
              aria-hidden
              style={{
                position: "relative",
                width: 62,
                height: 62,
                marginBottom: 6,
                display: "grid",
                placeItems: "center",
              }}
            >
              {/* the spinning gold arc = "still trying to reconnect" */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  border: "3px solid var(--hairline)",
                  borderTopColor: "var(--accent)",
                  animation: "bg-spin 1.1s linear infinite",
                }}
              />
              {/* wifi-off glyph = the opponent dropped */}
              <svg
                width="30"
                height="30"
                viewBox="0 0 24 24"
                fill="none"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ stroke: "var(--accent)" }}
              >
                <path d="M1 1l22 22" />
                <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
                <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
                <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
                <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
                <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
                <line x1="12" y1="20" x2="12.01" y2="20" />
              </svg>
            </div>
          )}
          <p style={{ margin: 0, fontWeight: 800, fontSize: 18 }}>{title}</p>
          {endsAt !== undefined ? (
            <div
              style={{
                border: "1px solid var(--accent)",
                borderRadius: 999,
                padding: "5px 20px",
                margin: "2px 0",
                fontVariantNumeric: "tabular-nums",
                fontSize: 20,
                fontWeight: 800,
                color: "var(--accent)",
              }}
            >
              {faCountdown(remaining)}
            </div>
          ) : null}
          {hint ? (
            <p className="muted" style={{ margin: "2px 0 6px", fontSize: 12.5 }}>
              {hint}
            </p>
          ) : null}
          {onClaim ? (
            <Button variant="primary" onClick={onClaim} style={{ width: "100%" }}>
              {fa.chess.claimWin}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Transient top banner: a player left the table. Non-blocking; the store auto-dismisses
 * it after a few seconds.
 *
 * Four things can have happened, and the notice must not lie about which:
 *  - a guest left mid-game → a bot took their chair;
 *  - the HOST left mid-game → a bot took their chair, the crown moved on, and — the
 *    thing everyone needs to know — THE GAME CARRIES ON (it used to freeze the whole
 *    table behind the pause overlay, waiting for a player who was never coming back);
 *  - the HOST left the waiting room → the seat is simply free (NO bot), and whoever
 *    inherited the crown must be told they can start;
 *  - a guest left the waiting room → silent (the roster empties in plain sight).
 *
 * …and ACROSS all of those, WHY the seat was lost. The server distinguishes a
 * deliberate «خروج» (`reason: "left"`) from a dropped connection whose grace elapsed
 * (`reason: "disconnected"`), but this notice used to ignore the field entirely and
 * announce both as «از میز رفت». On a mobile network a drop is the common case, and
 * telling the table someone WALKED OUT when their internet died is simply false.
 */
/**
 * A seat is mid-reconnect: a SMALL, NON-BLOCKING pill — deliberately not {@link OpponentAwayOverlay}.
 *
 * Chess and backgammon block the screen because there IS nothing to do until the opponent returns.
 * A card table is the opposite: the dropped seat is auto-played and the hand carries on, so a
 * full-screen overlay would freeze the other three for the whole 60s grace over something that
 * does not concern them. Same pill geometry as {@link LeftToast} — and it is REPLACED by that toast
 * if the grace expires and a bot takes the chair (the store clears `reconnecting` in the same
 * update that sets `leftNotice`), so the two can never stack.
 */
export function seatName(players: LobbyState["players"] | undefined, seat: number): string {
  const player = (players ?? []).find((p) => p.seat === seat);
  if (!player) return fa.online.somePlayer;
  // Same priority the roster itself uses (TableChat): the resolved identity, else the
  // server-resolved Telegram display name that already labels lobby seats — never an id.
  return (player.user ? displayName(player.user) : player.name) || fa.online.somePlayer;
}

/**
 * THE FREE EXIT, STANDING ON THE TABLE (operator, 2026-09-15).
 *
 * ⚠️ IT REPLACES A FULL-SCREEN SCRIM, AND THAT IS THE POINT. شطرنج and تخته نرد used to block the
 * whole board while an opponent was away, on the reasoning that there is nothing to do until they
 * return — but a player may well want to keep studying the position while they wait, and a
 * blocking overlay stops them. Same top-of-screen, `pointer-events:none` pill the card games have
 * always used.
 *
 * ⚠️ AND IT IS AN ANNOUNCEMENT, NOT A STANDING LINE (operator, 2026-09-15, reversing «it persists»
 * of the same morning). It was state-driven and untimed, and on a bot-filled حکم table that state
 * held from the deal to the end: the line sat on the felt's top row over the trump for the whole
 * match. It is shown for `FREE_EXIT_NOTICE_MS` after the grace-end takeover, on the two-seat board
 * games only — the gate is `OnlineGame`'s, the timer the store's.
 *
 * ⚠️ IT WRAPS. {@link ReconnectingToast} is `whiteSpace: nowrap` because «X در حال اتصال دوباره…»
 * is short; this sentence is not, and at 320px a nowrap pill would run off both ends.
 */
/**
 * THE ONE PLACE A TOP-OF-SCREEN NOTICE IS POSITIONED — below the header band, stacked, centred.
 *
 * ⚠️ EVERY TABLE PINS ITS OWN «خروج» AND «گفتگو» AT `calc(10px + safeTop)` IN A 40px BAND, AND
 * THESE PILLS USED TO BE PAINTED INTO IT. `FreeExitNotice` was moved clear of it on 2026-09-15
 * after the overlap was measured; `ReconnectingToast` and `LeftToast` were left at `+16px` on the
 * reasoning that they «clear that band by being SHORT and centred». They are short only when the
 * NAME is short. Measured on the deployed staging build with the widest name prod actually holds
 * (`probe-away-pill.cjs`, a real public two-seat table, four viewports): the reconnect pill read
 * `l 16…304` at 320, `l 16…344` at 360, `l 16…368` at 384 and `l 40…400` at 440 — over «خروج» at
 * EVERY width, and over «گفتگو» as well. `pointer-events:none` kept both buttons tappable, which
 * is the kind of «still works» that reads as broken.
 *
 * ⚠️ AND THEY STACK BECAUSE THEY CO-OCCUR — the takeover that fires `LeftToast` is the same frame
 * that arms the free-exit line, and a second drop can bring a reconnect pill while both are up.
 * Moving them to the same `top` INDEPENDENTLY would have traded a collision with the exit for a
 * collision with each other. One positioner, `flex-direction: column`, makes that impossible.
 * ⚠️ This comment used to justify the stack with «`lastHumanStanding` is true for the whole match
 * on any table that did not deal a full house, so the free-exit line can already be standing» —
 * which was the bug, written down as a design constraint: that line stood over the trump on every
 * bot-filled card table (2026-09-15). Nothing in this stack is permanent any more.
 * ⚠️ BELOW THE BAND IS ON THE FELT on the card tables: حکم's felt starts at ~`safeTop + 66px`, so
 * anything here covers its top row (the trump, the trick counter, the partner) while it is up.
 *
 * ⚠️ `left: 0; right: 0` + CENTRING FLEX, **NOT** `left: 50%` + `translateX(-50%)` — see
 * {@link FreeExitNotice}'s note: a WRAPPING pill under the translate trick is pinned at exactly
 * half the viewport. Renders nothing at all when it has no notice to show.
 */
export function TopNoticeStack({ children }: { children?: ReactNode }): ReactNode {
  const shown = Children.toArray(children).filter(Boolean);
  if (shown.length === 0) return null;
  return (
    <div
      style={{
        position: "fixed",
        // 58 = the band's own `10px + 40px` height, plus 8px of air. Measured against the exit's
        // painted box (`t 34 … b 74` at a 24px inset), not against the class that positions it.
        top: "calc(max(env(safe-area-inset-top), var(--tg-safe-top, 0px)) + 58px)",
        left: 0,
        right: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
        zIndex: 55,
        paddingInline: 16,
        pointerEvents: "none", // never intercept a tap — the board underneath stays playable
      }}
    >
      {shown}
    </div>
  );
}

export function FreeExitNotice(): ReactNode {
  return <NoticePill label={fa.online.awayFreeExitNotice} />;
}

/**
 * The server REFUSED the host's «شروع» / «بازی دوباره» and said why (today: maintenance, which stops
 * new games). The server's own sentence, on the same pill as {@link FreeExitNotice}.
 */
export function StartRefusedNotice({ message }: { message: string }): ReactNode {
  return <NoticePill label={message} />;
}

/** CARD-98 2.1 — a confirmed «خروج» is on its way (it may still wait for its socket and its verdict). */
export function LeavingNotice(): ReactNode {
  return <NoticePill label={fa.online.leaving} />;
}

/** The wrapping notice pill shared by {@link FreeExitNotice}, {@link StartRefusedNotice} and
 *  {@link LeavingNotice}. */
function NoticePill({ label }: { label: string }): ReactNode {
  /*
   * ⚠️ THE POSITIONER IS {@link TopNoticeStack}'S — this component is the PILL only, and the note
   * that used to stand here about `left: 0; right: 0` vs `left: 50%` moved there with it. It is
   * still load-bearing for this pill in particular: it is the one that WRAPS, and a wrapping box
   * laid out by shrink-to-fit with only `left` set is pinned at half the viewport (160px at 320,
   * six lines, every «does it fit» check green). Inside the stack its width is the flex item's
   * max-content clamped by the container, which is what the probe's «uses the width it has» leg
   * pins.
   */
  return (
    <div
      role="status"
      aria-label={label}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        maxWidth: "100%",
        padding: "10px 16px",
        borderRadius: 18,
        background: "var(--surface)",
        border: "1px solid var(--hairline)",
        color: "var(--text)",
        fontSize: 13,
        fontWeight: 700,
        boxShadow: "0 8px 22px rgba(0,0,0,.35)",
        animation: "bg-pop 0.3s ease",
        textAlign: "center",
        lineHeight: 1.6,
      }}
    >
      {/* ⚠️ NO EMOJI HERE (operator, 2026-09-15). A 🚪 at 13px beside Persian text rendered as an
            indistinct orange smudge at 320 — measured on a real screenshot, with the emoji font
            staged, so it was the glyph and not this host's usual tofu. The sentence carries itself;
            a smudge is worse than nothing. {@link ReconnectingToast}'s ⏳ is a different case — it
            is the only thing on a one-line pill and reads clearly. */}
      <span>{label}</span>
    </div>
  );
}

export function ReconnectingToast({ name }: { name: string }): ReactNode {
  const label = fa.online.reconnectingSeat(name);
  /*
   * ⚠️ POSITIONED BY {@link TopNoticeStack}, NOT BY ITSELF — it used to pin itself at
   * `safeTop + 16px`, inside the header band, and «it is short and centred» was true only of a
   * short NAME. `shortName(name, 14)` still caps the name, but 14 code points of the widest thing
   * prod holds measures ~298px of label against a 288px pill at 320: it saturates
   * `calc(100% - 32px)` and paints across «خروج» and «گفتگو» at every width.
   */
  return (
    <div
      role="status"
      aria-label={label}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        maxWidth: "100%",
        padding: "10px 16px",
        borderRadius: 999,
        background: "var(--surface)",
        border: "1px solid var(--hairline)",
        color: "var(--text)",
        fontSize: 13,
        fontWeight: 700,
        boxShadow: "0 8px 22px rgba(0,0,0,.35)",
        animation: "bg-pop 0.3s ease",
        whiteSpace: "nowrap",
      }}
    >
      <span aria-hidden>⏳</span>
      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
        {fa.online.reconnectingSeat(shortName(name, 14))}
      </span>
    </div>
  );
}

/**
 * WHAT THE SERVER'S `left` FRAME MEANS, IN ONE SENTENCE — shared, because the CARD tables now say
 * it on the seat (`seat-notice.tsx`) and keep this as that seat's accessible name, while every
 * other table paints it in {@link LeftToast}. Two copies of this ladder would be two chances to
 * tell a drop and a walk-out apart differently.
 *
 * `botTook` defaults TRUE: legacy servers only ever announced a mid-game bot takeover. `dropped`
 * (their socket died, the grace ran out — they did NOT choose to leave) and `idle` (a PUBLIC table
 * removed them for inactivity: they did not leave AND did not drop) are the two the copy insists on
 * keeping apart.
 */
export function leftSentence(notice: LeftInfo, name: string): string {
  const botTook = notice.botTook ?? true;
  const wasHost = notice.wasHost ?? false;
  const dropped = notice.reason === "disconnected";
  const idle = notice.reason === "idle";
  return wasHost
    ? botTook
      ? dropped
        ? fa.online.hostDisconnectedBot(name)
        : fa.online.hostLeftBot(name)
      : fa.online.hostLeftLobby(name)
    : botTook
      ? idle
        ? fa.online.idleReplacedByBot(name)
        : dropped
          ? fa.online.disconnectedByBot(name)
          : fa.online.replacedByBot(name)
      : fa.online.leftTable(name);
}

export function LeftToast({
  notice,
  isHost = false,
}: {
  notice: LeftInfo;
  /** Does the VIEWER hold the crown now? (After a host leaves, the server hands it on
   *  and tells that client first — so by now this is already the new truth.) */
  isHost?: boolean;
}): ReactNode {
  const wasHost = notice.wasHost ?? false;
  const sentence = (name: string): string => leftSentence(notice, name);
  // The DISPLAY caps the name so the sentence survives; the accessible label keeps
  // the whole name (only the rendering is shortened).
  const headline = sentence(shortName(notice.name, 14));
  const label = sentence(notice.name);
  /*
   * ⚠️ POSITIONED BY {@link TopNoticeStack} — this sentence is the LONGEST of the three
   * («ارتباط «X» قطع شد؛ ربات جایگزینش شد»), so it was the worst offender in the header band, and
   * it is the one that most often shares the screen with the free-exit line: the takeover that
   * fires it is the very moment the exit becomes free. The stack owns the centring, the 16px
   * gutters and the z-index (above the tables' HUD at z-40/50, below the win banner at z-60); the
   * entry animation stays on the pill, because `bg-pop` animates `transform` and would otherwise
   * clobber a centring transform for its whole 0.3s — the reported entry stutter.
   */
  return (
    <div
      role="status"
      aria-label={label}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        maxWidth: "100%",
        padding: "10px 16px",
        borderRadius: wasHost ? "var(--radius-lg)" : 999,
        background: "var(--surface)",
        border: "1px solid var(--hairline)",
        color: "var(--text)",
        fontSize: 13,
        fontWeight: 700,
        boxShadow: "0 8px 22px rgba(0,0,0,.35)",
        animation: "bg-pop 0.3s ease",
        // The guest notice is one short line (a pill). The HOST notice is TWO clean lines
        // (`\n` in the string → `pre-line`): who left, then that a bot took over and the game
        // goes on — an announcement nobody can read is not an announcement. Name is capped.
        whiteSpace: wasHost ? "pre-line" : "nowrap",
        textAlign: wasHost ? "center" : "start",
        overflow: "hidden",
      }}
    >
      <span aria-hidden style={{ flex: "none" }}>
        {wasHost ? "👑" : "🤖"}
      </span>
      {/* Cap the NAME (not the whole sentence) so «…از میز رفت؛ ربات جایگزینش شد»
            always stays visible on one line, even for a very long name. */}
      <span style={{ display: "grid", gap: 2, overflow: "hidden", textOverflow: "ellipsis" }}>
        <span>{headline}</span>
        {wasHost && isHost ? (
          <span style={{ color: "var(--accent)", fontSize: 12 }}>{fa.online.youAreHostNow}</span>
        ) : null}
      </span>
    </div>
  );
}

/**
 * THE HOST CLOSED THE TABLE (waiting room only).
 *
 * Their table, their call — but the people who accepted THEIR invite are owed an
 * explanation rather than a silently dead room, so the server announces it, the store
 * sounds it, and this says who did it and takes them back to the lobby. Mid-game is a
 * different story entirely: play carries on with a bot in the empty chair.
 */
/**
 * The table auto-EXPIRED: created but never started within the ~10-min idle window, so the
 * server hard-closed it. Shown to everyone who was seated (host + guests). Unlike the other
 * notices this does NOT auto-return: a table that sat idle for ten minutes almost certainly
 * means nobody is looking at the screen, so an auto-dismiss would race the notice away before
 * it could ever be read — the user must tap «بازگشت به لابی» themselves.
 */
export function TableExpiredNotice({ onLeave }: { onLeave: () => void }): ReactNode {
  return (
    <div
      className="end-overlay"
      role="alertdialog"
      aria-label={fa.online.tableExpiredTitle}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 58,
        display: "grid",
        placeItems: "center",
        padding: 20,
        background: "rgba(0,0,0,.55)",
      }}
    >
      <div
        className="end-card end-card--lose"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--hairline)",
          borderRadius: "var(--radius-lg)",
          padding: "28px 24px",
          width: "100%",
          maxWidth: 330,
          textAlign: "center",
          boxShadow: "0 30px 70px rgba(0,0,0,.5)",
        }}
      >
        <div style={{ fontSize: 46 }}>⌛</div>
        <div style={{ fontSize: 22, fontWeight: 800, margin: "8px 0 4px" }}>
          {fa.online.tableExpiredTitle}
        </div>
        <p style={{ margin: "0 0 18px", color: "var(--muted)" }}>{fa.online.tableExpiredBody}</p>
        <Button variant="primary" onClick={onLeave}>
          {fa.online.backToLobby}
        </Button>
      </div>
    </div>
  );
}

/**
 * YOU WERE REMOVED FROM A PUBLIC TABLE FOR INACTIVITY — the screen the 2026-07-23 incident was
 * missing.
 *
 * When the server hands an idle seat to a bot the player's SOCKET stays open, so their client goes
 * on receiving that seat's frames and watches a bot play the hand under their own name. Before,
 * that was the whole experience — a table playing on without them and a banner about "someone"
 * leaving. This replaces it with a screen that says what happened, in the second person, and gives
 * the one action that makes sense.
 *
 * DELIBERATELY NO AUTO-DISMISS and no way out except the button: dropping it on a timer would
 * hand the player back the frozen table it exists to cover.
 */
/**
 * THE ONE NOTICE A REMOVED PLAYER GETS.
 *
 * ⚠️ There used to be a SECOND, arriving later: 🤖 «جات به ربات سپرده شد» from `ConnectionOverlay`'s
 * `seatExpired` state, painted at z-70 over this card's z-62 because the connection was still being
 * managed and its eventual reconnect was refused. Fixed at the source — `online-connection.ts`
 * releases the connection when a `left {reason:"idle"}` names our own seat, exactly as it already
 * did for `gameover` / `table-closed` / `table-expired`. Do not add a second notice back.
 */
export function IdleRemovedNotice({ onAcknowledge }: { onAcknowledge: () => void }): ReactNode {
  return (
    <div
      className="end-overlay"
      role="alertdialog"
      aria-label={fa.online.idleRemovedTitle}
      style={{
        position: "fixed",
        inset: 0,
        // Above the win banner (z-60) and every table layer: nothing behind it is live any more,
        // so nothing behind it may take a tap.
        zIndex: 62,
        display: "grid",
        placeItems: "center",
        padding: 20,
        background: "rgba(0,0,0,.62)",
      }}
    >
      <div
        className="end-card end-card--lose"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--hairline)",
          borderRadius: "var(--radius-lg)",
          padding: "28px 24px",
          width: "100%",
          maxWidth: 330,
          textAlign: "center",
          boxShadow: "0 30px 70px rgba(0,0,0,.5)",
        }}
      >
        <div style={{ fontSize: 46 }}>💤</div>
        <div style={{ fontSize: 22, fontWeight: 800, margin: "8px 0 4px" }}>
          {fa.online.idleRemovedTitle}
        </div>
        <p style={{ margin: "0 0 18px", color: "var(--muted)", whiteSpace: "pre-line" }}>
          {fa.online.idleRemovedBody}
        </p>
        <Button variant="primary" onClick={onAcknowledge}>
          {fa.online.idleRemovedAck}
        </Button>
      </div>
    </div>
  );
}

/**
 * WHAT A PUBLIC-TABLE QUIT COST — shown AFTER «خروج بدون بازگشت» on a public table in progress
 * (2026-08-27). The socket is already gone by the time this renders; the only way out is the
 * button, so the count is actually read rather than flashing past on the way to the lobby.
 */
/**
 * THE HOST REMOVED US («اخراج», 2026-09-02) — the same covering treatment as
 * {@link IdleRemovedNotice}, and deliberately its OWN component rather than a `reason` prop on
 * that one: the two say different things. An idle removal is a rule the reader triggered; this is
 * somebody else's decision about them, so «you did not play for several turns» would be untrue in
 * the one place they are owed the truth. Everything else is shared on purpose — the table beneath
 * is equally not theirs any more, so the only exit is still the lobby.
 */
export function KickedNotice({ onAcknowledge }: { onAcknowledge: () => void }): ReactNode {
  return (
    <div
      className="end-overlay"
      role="alertdialog"
      aria-label={fa.online.kickedTitle}
      style={{
        position: "fixed",
        inset: 0,
        // Above the win banner (z-60) and every table layer: nothing behind it is live any more,
        // so nothing behind it may take a tap.
        zIndex: 62,
        display: "grid",
        placeItems: "center",
        padding: 20,
        background: "rgba(0,0,0,.62)",
      }}
    >
      <div
        className="end-card end-card--lose"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--hairline)",
          borderRadius: "var(--radius-lg)",
          padding: "28px 24px",
          width: "100%",
          maxWidth: 330,
          textAlign: "center",
          boxShadow: "0 30px 70px rgba(0,0,0,.5)",
        }}
      >
        <div style={{ fontSize: 46 }}>🚪</div>
        <div style={{ fontSize: 22, fontWeight: 800, margin: "8px 0 4px" }}>
          {fa.online.kickedTitle}
        </div>
        <p style={{ margin: "0 0 18px", color: "var(--muted)", whiteSpace: "pre-line" }}>
          {fa.online.kickedBody}
        </p>
        <Button variant="primary" onClick={onAcknowledge}>
          {fa.online.kickedAck}
        </Button>
      </div>
    </div>
  );
}

/**
 * WHAT A PUBLIC-TABLE QUIT COST — shown AFTER «خروج بدون بازگشت» on a public table in progress
 * (2026-08-27). The socket is already gone by the time this renders; the only way out is the
 * button, so the count is actually read rather than flashing past on the way to the lobby.
 */

export function LeavePenaltyNotice({
  info,
  onAcknowledge,
}: {
  info: LeavePenaltyInfo;
  onAcknowledge: () => void;
}): ReactNode {
  return (
    <div
      className="end-overlay"
      role="alertdialog"
      aria-label={fa.online.leavePenaltyTitle}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 62,
        display: "grid",
        placeItems: "center",
        padding: 20,
        background: "rgba(0,0,0,.62)",
      }}
    >
      <div
        className="end-card end-card--lose"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--hairline)",
          borderRadius: "var(--radius-lg)",
          padding: "28px 24px",
          width: "100%",
          maxWidth: 330,
          textAlign: "center",
          boxShadow: "0 30px 70px rgba(0,0,0,.5)",
        }}
      >
        <div style={{ fontSize: 46 }}>🚪</div>
        <div style={{ fontSize: 22, fontWeight: 800, margin: "8px 0 4px" }}>
          {fa.online.leavePenaltyTitle}
        </div>
        <p style={{ margin: "0 0 10px", color: "var(--muted)", whiteSpace: "pre-line" }}>
          {fa.online.leavePenaltyBody}
        </p>
        {info.leavesInWindow > 0 ? (
          <p style={{ margin: "0 0 10px", fontWeight: 700 }}>
            {fa.online.leavePenaltyCount(info.leavesInWindow)}
          </p>
        ) : null}
        {info.lockoutMs !== undefined ? (
          <p style={{ margin: "0 0 10px", color: "var(--danger, #e5564c)", fontWeight: 700 }}>
            {fa.online.leavePenaltyLockout(formatLockout(info.lockoutMs))}
          </p>
        ) : null}
        <Button variant="primary" onClick={onAcknowledge}>
          {fa.online.leavePenaltyAck}
        </Button>
      </div>
    </div>
  );
}

/**
 * LOCKED OUT of public online games — the gate refused the join. The BODY is the SERVER's own
 * sentence (it is the side that knows how long is left), so the remaining time can never drift
 * from what the server would enforce; the hint below it names what IS still open.
 */
export function LockoutNotice({
  message,
  onAcknowledge,
}: {
  message: string;
  onAcknowledge: () => void;
}): ReactNode {
  return (
    <div
      className="end-overlay"
      role="alertdialog"
      aria-label={fa.online.lockoutTitle}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 62,
        display: "grid",
        placeItems: "center",
        padding: 20,
        background: "rgba(0,0,0,.62)",
      }}
    >
      <div
        className="end-card"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--hairline)",
          borderRadius: "var(--radius-lg)",
          padding: "28px 24px",
          width: "100%",
          maxWidth: 330,
          textAlign: "center",
          boxShadow: "0 30px 70px rgba(0,0,0,.5)",
        }}
      >
        <div style={{ fontSize: 46 }}>⛔️</div>
        <div style={{ fontSize: 22, fontWeight: 800, margin: "8px 0 4px" }}>
          {fa.online.lockoutTitle}
        </div>
        <p style={{ margin: "0 0 8px", color: "var(--muted)", whiteSpace: "pre-line" }}>
          {message}
        </p>
        <p style={{ margin: "0 0 18px", color: "var(--muted)" }}>{fa.online.lockoutHint}</p>
        <Button variant="primary" onClick={onAcknowledge}>
          {fa.online.lockoutAck}
        </Button>
      </div>
    </div>
  );
}

/** «۴۵ دقیقه» / «۱ ساعت» — the same shape the server's own sentence uses. */
function formatLockout(ms: number): string {
  const minutes = Math.max(1, Math.ceil(ms / 60_000));
  if (minutes >= 60) return `${faLocalDigits(Math.round(minutes / 60))} ساعت`;
  return `${faLocalDigits(minutes)} دقیقه`;
}
const FA_LOCAL_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
function faLocalDigits(value: number): string {
  return String(value).replace(/\d/g, (d) => FA_LOCAL_DIGITS[Number(d)] ?? d);
}

export function TableClosedNotice({
  by,
  midGame = false,
  onLeave,
}: {
  by: string;
  /** The table was cancelled while a game was IN PROGRESS, not abandoned in the waiting room. */
  midGame?: boolean;
  onLeave: () => void;
}): ReactNode {
  // NO AUTO-DISMISS. This notice carries the only explanation a player will ever get for a game
  // that vanished out from under them, and a timed return takes it away before it has been read
  // — worse mid-game, where they were mid-hand. It leaves when THEY say so.
  const name = by.trim();
  const reason = midGame
    ? name
      ? fa.online.tableClosedMidGameBy(shortName(name, 16))
      : fa.online.tableClosedMidGameByHost
    : name
      ? fa.online.tableClosedBy(shortName(name, 16))
      : fa.online.tableClosedByHost;
  return (
    <div
      className="end-overlay"
      role="alertdialog"
      aria-label={fa.online.tableClosedTitle}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 58,
        display: "grid",
        placeItems: "center",
        padding: 20,
        background: "rgba(0,0,0,.55)",
      }}
    >
      <div
        className="end-card end-card--lose"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--hairline)",
          borderRadius: "var(--radius-lg)",
          padding: "28px 24px",
          width: "100%",
          maxWidth: 330,
          textAlign: "center",
          boxShadow: "0 30px 70px rgba(0,0,0,.5)",
        }}
      >
        <div style={{ fontSize: 46 }}>🚪</div>
        <div style={{ fontSize: 22, fontWeight: 800, margin: "8px 0 4px" }}>
          {midGame ? fa.online.tableClosedMidGameTitle : fa.online.tableClosedTitle}
        </div>
        <p style={{ margin: "0 0 18px", color: "var(--muted)", whiteSpace: "pre-line" }}>
          {reason}
        </p>
        <Button variant="primary" onClick={onLeave}>
          {fa.online.backToLobby}
        </Button>
      </div>
    </div>
  );
}
